import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from './store/authStore';

import AdminAnnouncements from './pages/admin/AdminAnnouncements';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import UserLayout from './layouts/UserLayout';
import AdminLayout from './layouts/AdminLayout';

// Public Pages
import Home from './pages/public/Home';
import Login from './pages/public/Login';
import AdminLogin from './pages/public/AdminLogin';
import ForgotPassword from './pages/public/ForgotPassword';
import ResetPassword from './pages/public/ResetPassword';
import Card from './pages/public/Card';

// User Pages
import UserDashboard from './pages/user/Dashboard';
import UserProfile from './pages/user/Profile';
import UserSecurity from './pages/user/Security';
import UserChangePassword from './pages/user/ChangePassword';
import CardPreview from './pages/user/CardPreview';
import EmailSignature from './pages/user/EmailSignature';
import CardAnalytics from './pages/user/CardAnalytics';
import Connections from './pages/user/Connections';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminUserDetail from './pages/admin/UserDetail';
import AdminCreateUser from './pages/admin/CreateUser';
import AdminSettings from './pages/admin/Settings';
import AdminActivity from './pages/admin/Activity';
import AdminAccountSettings from './pages/admin/AccountSettings';

// Components
import PrivateRoute from './components/PrivateRoute';
import LoadingScreen from './components/LoadingScreen';

function App() {
    const { checkAuth, isLoading } = useAuthStore();

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    if (isLoading) {
        return <LoadingScreen />;
    }

    return (
        <Routes>
            {/* Public card: full-screen, no site chrome */}
            <Route path="/card/:uuid" element={<Card />} />

            {/* Public Routes */}
            <Route path="/" element={<PublicLayout />}>
                <Route index element={<Home />} />
                <Route path="login" element={<Login />} />
                <Route path="admin/login" element={<AdminLogin />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="reset-password" element={<ResetPassword />} />
            </Route>

            {/* User Routes - Changed from /dashboard to /user */}
            <Route
                path="/user"
                element={
                    <PrivateRoute userType="user">
                        <UserLayout />
                    </PrivateRoute>
                }
            >
                <Route index element={<Navigate to="/user/dashboard" replace />} />
                <Route path="dashboard" element={<UserDashboard />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="security" element={<UserSecurity />} />
                <Route path="change-password" element={<UserChangePassword />} />
                <Route path="card-preview" element={<CardPreview />} />
                <Route path="analytics" element={<CardAnalytics />} />
                <Route path="connections" element={<Connections />} />
                <Route path="email-signature" element={<EmailSignature />} />
                <Route path="help" element={<div className="p-8">Help page coming soon...</div>} />
            </Route>

            {/* Admin Routes */}
            <Route
                path="/admin"
                element={
                    <PrivateRoute userType="admin">
                        <AdminLayout />
                    </PrivateRoute>
                }
            >
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="users/create" element={<AdminCreateUser />} />
                <Route path="users/:userId" element={<AdminUserDetail />} />
                <Route path="activity" element={<AdminActivity />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="account" element={<AdminAccountSettings />} />
                <Route path="announcements" element={<AdminAnnouncements />} />
            </Route>

            {/* Redirect old dashboard routes to new user routes */}
            <Route path="/dashboard" element={<Navigate to="/user/dashboard" replace />} />
            <Route path="/dashboard/*" element={<Navigate to="/user/dashboard" replace />} />

            {/* 404 Route */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default App;